import { TriangleAlert } from 'lucide-react';
import { useMemo } from 'react';
import { dataHealth, type Issue } from '@/lib/data-health';
import { tn } from '@/lib/i18n';
import { useNetwork } from '@/lib/network';
import { useLineTimetables } from '@/lib/timetables';

/**
 * Data health issues by the id of the line or direction they are about (the same checks as the
 * Data health page). Timetable gaps count for their direction.
 */
export function useIssuesById(kind: 'lines' | 'directions') {
  const network = useNetwork();
  const timetables = useLineTimetables();
  return useMemo(() => {
    const byId = new Map<string, Issue[]>();
    if (!network.data) return byId;
    const prefixes = kind === 'lines' ? ['/lines/'] : ['/directions/', '/schedules/directions/'];
    for (const issue of dataHealth(network.data, timetables.data?.items)) {
      const prefix = prefixes.find((p) => issue.link.startsWith(p));
      if (!prefix) continue;
      const id = issue.link.slice(prefix.length).split(/[/?#]/)[0]!;
      byId.set(id, [...(byId.get(id) ?? []), issue]);
    }
    return byId;
  }, [network.data, timetables.data, kind]);
}

/** Red for problems, amber when there is only something to check; the issues on hover. */
export function IssuesBadge({ issues }: { issues?: Issue[] }) {
  if (!issues?.length) return null;
  const problems = issues.filter((i) => i.severity === 'problem').length;
  const label = problems
    ? tn(problems, '{n} problem', '{n} problems')
    : tn(issues.length, '{n} to check', '{n} to check');
  return (
    <span
      title={issues.map((i) => `• ${i.text}`).join('\n')}
      className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
        problems ? 'bg-red-50 text-red-700 ring-red-600/20' : 'bg-amber-50 text-amber-800 ring-amber-600/20'
      }`}
    >
      <TriangleAlert className="size-3.5" />
      {label}
    </span>
  );
}
